import React from "react";
import { ClientErrorRecoveryView } from "./ClientErrorRecoveryView";
import { createClientErrorId } from "./clientErrorIds";
import type { ClientErrorRecoveryLabels, ClientErrorReporter } from "./clientErrorTypes";
import { buildReportedStack, formatErrorCause, readErrorRequestId, toReportableError } from "./clientErrorTypes";
import { readErrorLocation, readModuleHint } from "./clientErrorReportText";
import { getCurrentPath } from "./currentPath";

type ClientErrorBoundaryProps = {
  children: React.ReactNode;
  labels: ClientErrorRecoveryLabels;
  showDeveloperDetails?: boolean;
  onBackToDashboard: () => void;
  onReload?: () => void;
  reportError: ClientErrorReporter;
};

type ClientErrorBoundaryState = {
  error: Error | null;
  errorId: string | null;
  errorInfo: React.ErrorInfo | null;
  hasError: boolean;
};

export class ClientErrorBoundary extends React.Component<ClientErrorBoundaryProps, ClientErrorBoundaryState> {
  state: ClientErrorBoundaryState = {
    error: null,
    errorId: null,
    errorInfo: null,
    hasError: false,
  };

  static getDerivedStateFromError(thrown: unknown): Partial<ClientErrorBoundaryState> {
    return { error: toReportableError(thrown), hasError: true };
  }

  componentDidCatch(thrown: unknown, errorInfo: React.ErrorInfo) {
    const error = toReportableError(thrown);
    const errorId = createClientErrorId("ERR");
    this.setState({ error, errorId, errorInfo });
    this.props.reportError({
      errorId,
      message: error.message?.trim() || "React render error (no message)",
      stack: buildReportedStack(error),
      componentStack: errorInfo.componentStack ?? null,
      sourceKind: "ClientCrash",
    });
  }

  reset = () => {
    this.setState({ error: null, errorId: null, errorInfo: null, hasError: false });
  };

  backToDashboard = () => {
    this.reset();
    this.props.onBackToDashboard();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const componentStack = this.state.errorInfo?.componentStack ?? null;
    const errorLocation = readErrorLocation(this.state.error?.stack, componentStack);
    const reportInput = {
      componentStack,
      currentPath: getCurrentPath(),
      errorCause: formatErrorCause(this.state.error),
      errorId: this.state.errorId,
      errorLocation,
      errorMessage: this.state.error?.message ?? null,
      moduleHint: readModuleHint(errorLocation),
      requestId: readErrorRequestId(this.state.error),
      stack: this.state.error?.stack ?? null,
    };

    return (
      <ClientErrorRecoveryView
        errorMessage={this.state.error?.message ?? null}
        labels={this.props.labels}
        mode="page"
        reportInput={reportInput}
        showDeveloperDetails={this.props.showDeveloperDetails}
        stack={this.state.error?.stack ?? null}
        onBackToDashboard={this.backToDashboard}
        onReload={this.props.onReload}
      />
    );
  }
}
